import { StudyDemoIdentityStrip } from '@/pages/try/studyExperience/StudyDemoIdentityStrip';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

import { recordHostedInteraction } from '@/lib/studyToolTelemetry';
import { ApiError } from '@/lib/api';
import { loadPdfJs } from './pdfJsLoader';
import { usePdfFullscreen } from './usePdfFullscreen';
import { useWorkingViewport } from './workingOverlay';
import { StudyPdfToolbar } from './StudyPdfToolbar';
import { StudyPdfMarkLayer } from './StudyPdfMarkLayer';
import { StudyPdfNotesPanel } from './StudyPdfNotesPanel';
import { StudyPdfWorkspaceTutor } from './StudyPdfWorkspaceTutor';
import { TutorSectionTabs } from '@/components/tutor/TutorSectionTabs';
import type { PdfColour, PdfDraft, PdfTool } from './studyPdfPalette';
import {
  moveGeometry,
  rectangle,
  unrotatePoint,
  type QuarterTurns,
} from './studyPdfAnnotationGeometry';
import {
  createStudyPdfAnnotation,
  deleteStudyPdfAnnotation,
  listStudyPdfAnnotations,
  updateStudyPdfAnnotation,
  type StudyPdfAnnotation,
  type StudyPdfAnnotationInput,
} from './studyApi';

type Point = { x: number; y: number };
type Tool = PdfTool;
type Draft = PdfDraft;
type AnnotationPayload =
  | { kind: 'ink'; geometry: { points: Point[]; colour: PdfColour; width: number } }
  | {
      kind: 'highlight';
      geometry: { x: number; y: number; width: number; height: number; colour: PdfColour };
    }
  | { kind: 'note'; geometry: Point; body: string };
type HistoryAction =
  | { kind: 'delete'; item: StudyPdfAnnotation }
  | { kind: 'restore'; item: StudyPdfAnnotation; requestId: string }
  | { kind: 'update'; current: StudyPdfAnnotation; target: StudyPdfAnnotation };
const keyFor = (documentId: string, pageNumber: number) =>
  ['study-pdf-annotations', documentId, pageNumber] as const;

function pointFor(event: PointerEvent<SVGSVGElement>): Point {
  const rect = event.currentTarget.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
    y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
  };
}

function explain(error: unknown): string {
  if (error instanceof ApiError && error.code === 'STUDY_PDF_UPLOAD_SETTLING') {
    return 'This older upload is still settling. You can read it now; try annotations again in a few minutes.';
  }
  if (error instanceof ApiError && error.status === 409) {
    return 'This annotation changed in another tab. Reload this page and try again.';
  }
  return error instanceof Error ? error.message : 'Your change could not be saved. Try again.';
}

export function StudyPdfAnnotationViewer({
  documentId,
  readUrl,
  fileName,
  sourceRevision,
  onUnsavedChange,
  tutorRequest = 0,
  onTutorOpenChange,
  onBack,
  mobileLearningAction,
}: {
  documentId: string;
  readUrl: string;
  fileName: string;
  sourceRevision: string;
  onUnsavedChange?: (unsaved: boolean) => void;
  tutorRequest?: number;
  onTutorOpenChange?: (open: boolean) => void;
  onBack?: () => void;
  mobileLearningAction?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const fullscreen = usePdfFullscreen();
  const viewport = useWorkingViewport(true);
  const [notesOpen, setNotesOpen] = useState(
    () => window.matchMedia?.('(min-width: 768px)').matches ?? true,
  );
  const [panel, setPanel] = useState<'notes' | 'tutor'>('notes');
  const [tutorVisited, setTutorVisited] = useState(false);
  const [tutorBusy, setTutorBusy] = useState(false);
  useEffect(() => {
    onTutorOpenChange?.(notesOpen && panel === 'tutor');
    return () => onTutorOpenChange?.(false);
  }, [notesOpen, panel, onTutorOpenChange]);
  const [inkColour, setInkColour] = useState<PdfColour>('coral');
  const [highlightColour, setHighlightColour] = useState<PdfColour>('yellow');
  const [penWidth, setPenWidth] = useState(0.004);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    if (tutorRequest) {
      setPanel('tutor');
      setTutorVisited(true);
      setNotesOpen(true);
    }
  }, [tutorRequest]);
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeStrokePointerId = useRef<number | null>(null);
  const touchPan = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const touchAvailable =
    typeof navigator !== 'undefined' &&
    (navigator.maxTouchPoints > 0 ||
      (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches));
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [hostWidth, setHostWidth] = useState(720);
  const [pageNumber, setPageNumber] = useState(1);
  const [rotation, setRotation] = useState<QuarterTurns>(0);
  const [zoom, setZoom] = useState(1);
  const [rendering, setRendering] = useState(true);
  const [tool, setTool] = useState<Tool>('read');
  const [fingerWriting, setFingerWriting] = useState(() =>
    Boolean(window.matchMedia?.('(max-width: 767px) and (pointer: coarse)').matches),
  );
  const [draft, setDraft] = useState<Draft>(null);
  const [notePoint, setNotePoint] = useState<Point | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [movingItem, setMovingItem] = useState<StudyPdfAnnotation | null>(null);
  const [feedback, setFeedbackText] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState(false);
  const setFeedback = useCallback((value: string | null) => {
    setFeedbackText(value);
    setFeedbackError(false);
  }, []);
  const reportFailure = (error: unknown) => {
    setFeedbackText(explain(error));
    setFeedbackError(true);
  };
  const [unsaved, setUnsaved] = useState<StudyPdfAnnotationInput | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadedMore, setLoadedMore] = useState(false);
  const [extra, setExtra] = useState<StudyPdfAnnotation[]>([]);
  const [undoStack, setUndoStack] = useState<HistoryAction[]>([]);
  const [redoStack, setRedoStack] = useState<HistoryAction[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setHostWidth(host.clientWidth));
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    let task: PDFDocumentLoadingTask | null = null;
    setPdf(null);
    setPageNumber(1);
    setZoom(1);
    setPdfError(null);
    void loadPdfJs()
      .then(({ getDocument }) => {
        if (!active) return;
        // Keep the 30 MB maximum PDF in memory after the short-lived signed URL
        // expires, so moving to a later page does not issue a stale range GET.
        task = getDocument({ url: readUrl, disableRange: true, disableStream: true });
        return task.promise.then((loaded) => {
          if (active) setPdf(loaded);
          else void loaded.destroy();
        });
      })
      .catch(() => {
        if (active)
          setPdfError('This PDF could not be opened here. Open the original file instead.');
      });
    return () => {
      active = false;
      void task?.destroy();
    };
  }, [readUrl]);

  useEffect(() => {
    if (!pdf) return;
    let active = true;
    let task: RenderTask | null = null;
    setRendering(true);
    void pdf
      .getPage(pageNumber)
      .then((page) => {
        if (!active || !canvasRef.current) return;
        const base = page.getViewport({ scale: 1, rotation: page.rotate + rotation * 90 });
        const available = Math.max(240, Math.min(1000, hostWidth - 32));
        const viewport = page.getViewport({
          scale: Math.min(2, available / base.width) * zoom,
          rotation: page.rotate + rotation * 90,
        });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas unavailable');
        const outputScale = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        task = page.render({
          canvas,
          canvasContext: context,
          viewport,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
        });
        return task.promise;
      })
      .then(() => {
        if (active) setRendering(false);
      })
      .catch((reason: unknown) => {
        if (active && (reason as { name?: string }).name !== 'RenderingCancelledException') {
          setPdfError('This page could not be displayed. Open the original file instead.');
          setRendering(false);
        }
      });
    return () => {
      active = false;
      task?.cancel();
    };
  }, [pdf, pageNumber, rotation, zoom, hostWidth]);

  useEffect(() => {
    activeStrokePointerId.current = null;
    touchPan.current = null;
    setDraft(null);
    setNotePoint(null);
    setEditingId(null);
    setMovingItem(null);
    setFeedback(null);
    setUnsaved(null);
    setCursor(null);
    setLoadedMore(false);
    setExtra([]);
    setUndoStack([]);
    setRedoStack([]);
  }, [documentId, pageNumber, setFeedback]);

  const annotations = useQuery({
    queryKey: keyFor(documentId, pageNumber),
    queryFn: () => listStudyPdfAnnotations(documentId, pageNumber),
    enabled: Boolean(pdf && sourceRevision),
    retry: false,
  });
  const items = [...(annotations.data?.items ?? []), ...extra];
  const record = (inverse: HistoryAction) => {
    setUndoStack((current) => [...current.slice(-49), inverse]);
    setRedoStack([]);
  };
  const save = useMutation({
    mutationFn: (input: StudyPdfAnnotationInput) => createStudyPdfAnnotation(documentId, input),
    onSuccess: async (created) => {
      record({ kind: 'delete', item: created });
      setUnsaved(null);
      setNoteBody('');
      setNotePoint(null);
      recordHostedInteraction('pdf-annotation', 'annotation_added');
      setFeedback('Saved privately to this PDF page.');
      await queryClient.invalidateQueries({ queryKey: keyFor(documentId, pageNumber) });
    },
    onError: (error, input) => {
      setPanel('notes');
      setNotesOpen(true);
      setUnsaved(input);
      reportFailure(error);
    },
  });
  const remove = useMutation({
    mutationFn: (item: StudyPdfAnnotation) =>
      deleteStudyPdfAnnotation(documentId, item.id, item.version),
    onSuccess: async (_result, item) => {
      record({ kind: 'restore', item, requestId: crypto.randomUUID() });
      recordHostedInteraction('pdf-annotation', 'annotation_added');
      setFeedback('Annotation deleted.');
      setExtra([]);
      await queryClient.invalidateQueries({ queryKey: keyFor(documentId, pageNumber) });
    },
    onError: (error) => reportFailure(error),
  });
  const edit = useMutation({
    mutationFn: (item: StudyPdfAnnotation) =>
      updateStudyPdfAnnotation(documentId, item.id, item.version, { body: noteBody }),
    onSuccess: async (updated, item) => {
      record({ kind: 'update', current: updated, target: item });
      setEditingId(null);
      setNoteBody('');
      recordHostedInteraction('pdf-annotation', 'annotation_added');
      setFeedback('Note saved.');
      await queryClient.invalidateQueries({ queryKey: keyFor(documentId, pageNumber) });
    },
    onError: (error) => reportFailure(error),
  });
  const relocate = useMutation({
    mutationFn: ({ item, target }: { item: StudyPdfAnnotation; target: Point }) =>
      updateStudyPdfAnnotation(documentId, item.id, item.version, {
        geometry: moveGeometry(item, target),
      }),
    onSuccess: async (updated, { item }) => {
      record({ kind: 'update', current: updated, target: item });
      setMovingItem(null);
      recordHostedInteraction('pdf-annotation', 'annotation_added');
      setFeedback('Annotation moved.');
      await queryClient.invalidateQueries({ queryKey: keyFor(documentId, pageNumber) });
    },
    onError: (error) => reportFailure(error),
  });
  const recolour = useMutation({
    mutationFn: ({ item, colour }: { item: StudyPdfAnnotation; colour: PdfColour }) =>
      updateStudyPdfAnnotation(documentId, item.id, item.version, {
        geometry: { ...item.geometry, colour },
      }),
    onSuccess: async (updated, { item }) => {
      record({ kind: 'update', current: updated, target: item });
      setExtra([]);
      setFeedback('Colour saved.');
      await queryClient.invalidateQueries({ queryKey: keyFor(documentId, pageNumber) });
    },
    onError: (error) => reportFailure(error),
  });
  const busy =
    save.isPending ||
    edit.isPending ||
    remove.isPending ||
    relocate.isPending ||
    recolour.isPending ||
    historyBusy;
  const hasLocalDraft = (notePoint !== null || editingId !== null) && noteBody.trim().length > 0;
  useEffect(
    () => onUnsavedChange?.(unsaved !== null || busy || hasLocalDraft || tutorBusy),
    [unsaved, busy, hasLocalDraft, tutorBusy, onUnsavedChange],
  );

  const performHistory = async (action: HistoryAction): Promise<HistoryAction> => {
    if (action.kind === 'delete') {
      await deleteStudyPdfAnnotation(documentId, action.item.id, action.item.version);
      return { kind: 'restore', item: action.item, requestId: crypto.randomUUID() };
    }
    if (action.kind === 'restore') {
      const restored = await createStudyPdfAnnotation(documentId, {
        request_id: action.requestId,
        source_revision: action.item.source_revision,
        page_number: action.item.page_number,
        kind: action.item.kind,
        geometry: action.item.geometry,
        ...(action.item.kind === 'note' ? { body: action.item.body } : {}),
      } as StudyPdfAnnotationInput);
      return { kind: 'delete', item: restored };
    }
    const updated = await updateStudyPdfAnnotation(
      documentId,
      action.current.id,
      action.current.version,
      {
        geometry: action.target.geometry,
        ...(action.target.kind === 'note' && action.target.body
          ? { body: action.target.body }
          : {}),
      },
    );
    return { kind: 'update', current: updated, target: action.current };
  };

  const stepHistory = async (direction: 'undo' | 'redo') => {
    const stack = direction === 'undo' ? undoStack : redoStack;
    const action = stack.at(-1);
    if (!action || busy || unsaved) return;
    setHistoryBusy(true);
    try {
      const inverse = await performHistory(action);
      if (direction === 'undo') {
        setUndoStack((current) => current.slice(0, -1));
        setRedoStack((current) => [...current, inverse]);
      } else {
        setRedoStack((current) => current.slice(0, -1));
        setUndoStack((current) => [...current, inverse]);
      }
      setExtra([]);
      recordHostedInteraction('pdf-annotation', 'annotation_added');
      setFeedback(direction === 'undo' ? 'Change undone.' : 'Change redone.');
      await queryClient.invalidateQueries({ queryKey: keyFor(documentId, pageNumber) });
    } catch (error) {
      reportFailure(error);
    } finally {
      setHistoryBusy(false);
    }
  };

  const create = (input: AnnotationPayload) => {
    if (!sourceRevision || save.isPending || unsaved) return;
    save.mutate({
      ...input,
      request_id: crypto.randomUUID(),
      source_revision: sourceRevision,
      page_number: pageNumber,
    } as StudyPdfAnnotationInput);
  };

  const startTouchPan = (event: PointerEvent<HTMLDivElement>) => {
    if (
      event.pointerType !== 'touch' ||
      fingerWriting ||
      (tool === 'read' && !movingItem) ||
      activeStrokePointerId.current !== null
    )
      return;
    touchPan.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const moveTouchPan = (event: PointerEvent<HTMLDivElement>) => {
    const pan = touchPan.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    event.currentTarget.scrollLeft -= event.clientX - pan.x;
    event.currentTarget.scrollTop -= event.clientY - pan.y;
    touchPan.current = { pointerId: pan.pointerId, x: event.clientX, y: event.clientY };
  };
  const endTouchPan = (event: PointerEvent<HTMLDivElement>) => {
    if (touchPan.current?.pointerId === event.pointerId) touchPan.current = null;
  };

  const pointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if ((tool === 'read' && !movingItem) || busy || unsaved || rendering || annotations.isError)
      return;
    if (
      !fingerWriting &&
      (event.pointerType === 'touch' ||
        (touchAvailable && event.pointerType !== 'pen' && event.pointerType !== 'mouse'))
    )
      return;
    if (activeStrokePointerId.current !== null) return;
    if (event.pointerType === 'pen') touchPan.current = null;
    const point = unrotatePoint(pointFor(event), rotation);
    if (tool === 'erase') {
      const id = (event.target as Element).getAttribute('data-annotation-id');
      const item = items.find((candidate) => candidate.id === id);
      if (item) remove.mutate(item);
      return;
    }
    if (movingItem) {
      relocate.mutate({ item: movingItem, target: point });
      return;
    }
    if (tool === 'note') {
      if (hasLocalDraft) {
        setFeedback('Save or cancel your current note first.');
        return;
      }
      setPanel('notes');
      setNotesOpen(true);
      setNotePoint(point);
      setNoteBody('');
      setEditingId(null);
      return;
    }
    activeStrokePointerId.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraft(
      tool === 'ink'
        ? { kind: 'ink', points: [point], colour: inkColour, width: penWidth }
        : { kind: 'highlight', start: point, end: point, colour: highlightColour },
    );
  };
  const pointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (!draft || activeStrokePointerId.current !== event.pointerId) return;
    const point = unrotatePoint(pointFor(event), rotation);
    setDraft(
      draft.kind === 'ink'
        ? { ...draft, points: [...draft.points, point].slice(0, 1500) }
        : { ...draft, end: point },
    );
  };
  const pointerUp = (event: PointerEvent<SVGSVGElement>) => {
    if (!draft || activeStrokePointerId.current !== event.pointerId) return;
    activeStrokePointerId.current = null;
    const end = unrotatePoint(pointFor(event), rotation);
    if (draft.kind === 'ink') {
      const points = [...draft.points, end].slice(0, 1500);
      if (points.length >= 2)
        create({ kind: 'ink', geometry: { points, colour: draft.colour, width: draft.width } });
    } else {
      const rect = rectangle(draft.start, end);
      if (rect.width > 0.005 && rect.height > 0.005)
        create({ kind: 'highlight', geometry: { ...rect, colour: draft.colour } });
    }
    setDraft(null);
  };
  const pointerCancel = (event: PointerEvent<SVGSVGElement>) => {
    if (activeStrokePointerId.current === event.pointerId) {
      activeStrokePointerId.current = null;
      setDraft(null);
    }
  };

  return (
    <div
      ref={fullscreen.rootRef}
      data-study-tool={pdf && !rendering ? 'pdf-annotation' : undefined}
      data-study-semantic="annotation"
      style={
        {
          '--pdf-mobile-height': `${viewport.height}px`,
          '--pdf-mobile-top': `${viewport.top}px`,
        } as CSSProperties
      }
      className={`flex min-h-0 flex-col overflow-hidden bg-canvas-pure ${notesOpen && panel === 'tutor' && (fullscreen.active || !onTutorOpenChange) ? 'md:pr-[420px]' : ''} max-md:bottom-auto max-md:top-[var(--pdf-mobile-top)] max-md:h-[var(--pdf-mobile-height)] ${fullscreen.active ? 'fixed inset-0 z-[100] h-dvh w-full md:pb-[env(safe-area-inset-bottom)] md:pt-[env(safe-area-inset-top)]' : 'h-full rounded-2xl border border-hairline max-md:fixed max-md:inset-0 max-md:z-40 max-md:rounded-none max-md:border-0'}`}
    >
      <StudyDemoIdentityStrip className={fullscreen.active ? '' : 'md:hidden'} />
      {fullscreen.active && (
        <p className="hidden shrink-0 truncate border-b border-hairline px-4 py-2 text-sm font-bold text-ink md:block">
          {fileName}
        </p>
      )}
      <StudyPdfToolbar
        fileName={fileName}
        panel={panel}
        onBack={onBack}
        mobileLearningAction={fullscreen.active ? undefined : mobileLearningAction}
        onPanel={(value) => {
          setPanel(value);
          setNotesOpen(true);
          if (value === 'tutor') setTutorVisited(true);
        }}
        page={pageNumber}
        pages={pdf?.numPages ?? 0}
        zoom={zoom}
        loading={!pdf || rendering}
        locked={busy || Boolean(unsaved) || hasLocalDraft || tutorBusy}
        drawing={Boolean(draft)}
        tool={tool}
        colour={tool === 'highlight' ? highlightColour : inkColour}
        width={penWidth}
        canUndo={undoStack.length > 0}
        canRedo={redoStack.length > 0}
        fullscreen={fullscreen}
        notesOpen={notesOpen}
        onPage={setPageNumber}
        onZoom={setZoom}
        onRotate={() => setRotation((current) => ((current + 1) % 4) as QuarterTurns)}
        onHistory={(direction) => void stepHistory(direction)}
        onTool={(value) => {
          if (value === 'note') {
            setPanel('notes');
            setNotesOpen(true);
          }
          const phone = window.matchMedia?.('(max-width: 767px)').matches;
          if (phone) {
            if (value === 'note') {
              setNotePoint({ x: 0.5, y: 0.08 });
              setNoteBody('');
              setEditingId(null);
            } else setNotesOpen(false);
          }
          setTool(value);
          setDraft(null);
          activeStrokePointerId.current = null;
          touchPan.current = null;
          setMovingItem(null);
        }}
        onColour={tool === 'highlight' ? setHighlightColour : setInkColour}
        onWidth={setPenWidth}
        onToggleNotes={() => setNotesOpen((open) => !open)}
      />
      {touchAvailable && (
        <div className="hidden flex-wrap items-center justify-between gap-2 border-b border-hairline bg-wash-sky px-3 py-2 text-xs text-ink md:flex">
          <p>
            {fingerWriting
              ? 'Finger writing is on. Choose Read to move the page.'
              : 'On iPad: Apple Pencil marks the PDF. Use a finger to move the page.'}{' '}
            Use the zoom buttons to see details.
          </p>
          <button
            type="button"
            aria-pressed={fingerWriting}
            onClick={() => {
              setFingerWriting((current) => !current);
              setDraft(null);
              activeStrokePointerId.current = null;
              touchPan.current = null;
            }}
            className="min-h-11 rounded-full border border-ink px-4 font-bold"
          >
            Finger writing {fingerWriting ? 'on' : 'off'}
          </button>
        </div>
      )}
      {fullscreen.notice && (
        <p
          role="status"
          className="border-b border-hairline bg-wash-sky px-3 py-2 text-sm text-ink"
        >
          {fullscreen.notice}
        </p>
      )}
      <div className="relative flex min-h-0 flex-1 flex-col md:flex-row">
        <div
          ref={hostRef}
          role="region"
          aria-label="PDF page"
          onPointerDownCapture={startTouchPan}
          onPointerMoveCapture={moveTouchPan}
          onPointerUpCapture={endTouchPan}
          onPointerCancelCapture={endTouchPan}
          className="min-h-0 flex-1 overflow-auto overscroll-contain bg-surface p-2 text-center md:p-3"
        >
          {pdfError && (
            <p role="alert" className="p-5 text-sm font-bold text-ink">
              {pdfError}{' '}
              <a href={readUrl} target="_blank" rel="noreferrer" className="underline">
                Open original PDF
              </a>
            </p>
          )}
          {!pdfError && !pdf && (
            <p className="p-5 text-sm text-slate2">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              Opening PDF…
            </p>
          )}
          <div
            className="relative mx-auto inline-block bg-white shadow-md"
            aria-label={`${fileName}, page ${pageNumber}`}
          >
            <canvas ref={canvasRef} className="block" />
            {pdf && !rendering && (
              <svg
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                className={`absolute inset-0 h-full w-full ${tool === 'read' && !movingItem ? 'pointer-events-none' : 'cursor-crosshair touch-none'}`}
                onPointerDown={pointerDown}
                onPointerMove={pointerMove}
                onPointerUp={pointerUp}
                onPointerCancel={pointerCancel}
                aria-label="PDF annotation layer"
              >
                <StudyPdfMarkLayer
                  items={items}
                  draft={draft}
                  notePoint={notePoint}
                  rotation={rotation}
                />
              </svg>
            )}
          </div>
        </div>
        <aside
          id={`pdf-page-notes-${documentId}`}
          hidden={!notesOpen}
          className={`${notesOpen ? 'flex' : 'hidden'} absolute inset-x-0 bottom-0 z-10 w-full shrink-0 flex-col ${panel === 'tutor' ? `${isLocalStudyExperience() ? 'h-[calc(100%_-_44px)]' : 'h-full'} rounded-none md:fixed md:inset-y-0 md:left-auto md:right-0 md:z-[110] md:h-dvh md:w-[420px]` : `${notePoint || editingId ? 'h-[90%]' : 'h-[58%]'} rounded-t-3xl md:relative md:inset-auto md:z-auto md:h-auto md:w-80 xl:w-[360px]`} min-h-0 overflow-hidden border-t border-hairline bg-canvas-pure shadow-floating-chrome md:max-h-none md:rounded-none md:border-l md:border-t-0 md:shadow-none`}
          aria-label="Page notes"
        >
          <button
            type="button"
            aria-label="Collapse page panel"
            onClick={() => setNotesOpen(false)}
            className="flex h-7 shrink-0 items-center justify-center rounded-t-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-sky md:hidden"
          >
            <span className="h-1 w-9 rounded-full bg-stone2" />
          </button>
          <div
            className={`hidden shrink-0 border-b border-hairline px-3 pb-2 md:block ${panel === 'tutor' ? 'md:p-2' : 'md:p-3'}`}
          >
            <TutorSectionTabs
              label="PDF workspace"
              value={panel}
              items={[
                { id: 'notes', label: 'Notes' },
                { id: 'tutor', label: 'Tutor' },
              ]}
              panelId={`pdf-workspace-${documentId}`}
              onChange={(value) => {
                setPanel(value);
                if (value === 'tutor') setTutorVisited(true);
              }}
            />
            {!notePoint && !editingId && panel !== 'tutor' && (
              <p className="mt-2 hidden px-1 text-[11px] text-slate2 md:block">
                Page {pageNumber} · private learning workspace
              </p>
            )}
          </div>
          <div
            id={`pdf-workspace-${documentId}`}
            role="tabpanel"
            aria-labelledby={`pdf-workspace-${documentId}-tab-${panel}`}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div
              hidden={panel !== 'notes'}
              className={panel === 'notes' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
            >
              <StudyPdfNotesPanel
                items={items}
                loading={annotations.isLoading}
                error={annotations.isError ? explain(annotations.error) : null}
                busy={busy}
                locked={busy || Boolean(unsaved)}
                editingId={editingId}
                movingItem={movingItem}
                notePoint={notePoint}
                noteBody={noteBody}
                unsaved={unsaved}
                feedback={feedback}
                feedbackError={feedbackError}
                hasMore={Boolean(loadedMore ? cursor : annotations.data?.next_cursor)}
                loadingMore={loadingMore}
                onAdd={() => {
                  setTool('note');
                  setNotePoint({ x: 0.5, y: 0.08 });
                  setNoteBody('');
                  setEditingId(null);
                }}
                onRetry={() => void annotations.refetch()}
                onMove={(item) => {
                  setMovingItem(item);
                  setTool('read');
                  if (window.matchMedia?.('(max-width: 767px)').matches) setNotesOpen(false);
                  setFeedback('Click the PDF page to move this annotation.');
                }}
                onDelete={(item) => remove.mutate(item)}
                onEdit={(item) => {
                  setEditingId(item.id);
                  setNoteBody(item.body ?? '');
                }}
                onColour={(item, colour) => recolour.mutate({ item, colour })}
                onBody={setNoteBody}
                onSave={() => {
                  const item = items.find((value) => value.id === editingId);
                  if (item) edit.mutate(item);
                  else if (notePoint)
                    create({ kind: 'note', geometry: notePoint, body: noteBody.trim() });
                }}
                onCancel={() => {
                  setNotePoint(null);
                  setEditingId(null);
                  setNoteBody('');
                }}
                onCancelMove={() => setMovingItem(null)}
                onRetrySave={() => save.mutate(unsaved!)}
                onDiscard={() => {
                  setUnsaved(null);
                  setFeedback(null);
                  setNotePoint(null);
                  setNoteBody('');
                }}
                onLoadMore={() => {
                  void (async () => {
                    const next = loadedMore ? cursor : annotations.data?.next_cursor;
                    if (!next || loadingMore) return;
                    setLoadingMore(true);
                    try {
                      const result = await listStudyPdfAnnotations(documentId, pageNumber, next);
                      setExtra((current) => [...current, ...result.items]);
                      setLoadedMore(true);
                      setCursor(result.next_cursor);
                    } catch (error) {
                      reportFailure(error);
                    } finally {
                      setLoadingMore(false);
                    }
                  })();
                }}
              />
            </div>
            <div
              hidden={panel !== 'tutor'}
              className={panel === 'tutor' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
            >
              {tutorVisited && (
                <StudyPdfWorkspaceTutor
                  key={documentId}
                  onPage={(page) => {
                    if (hasLocalDraft || unsaved || busy) {
                      setFeedback('Save or cancel your current note first.');
                      return;
                    }
                    if (page >= 1 && page <= (pdf?.numPages ?? 0)) setPageNumber(page);
                  }}
                  documentId={documentId}
                  pageNumber={pageNumber}
                  fileName={fileName}
                  active={panel === 'tutor' && notesOpen}
                  ready={Boolean(pdf && !rendering && !pdfError)}
                  onBusyChange={setTutorBusy}
                  preparePage={() =>
                    new Promise<Blob>((resolve, reject) => {
                      const canvas = canvasRef.current;
                      if (!canvas || rendering || !pdf) {
                        reject(new Error('Wait for the PDF page to finish opening.'));
                        return;
                      }
                      canvas.toBlob(
                        (blob) =>
                          blob
                            ? resolve(blob)
                            : reject(new Error('This PDF page could not be prepared.')),
                        'image/png',
                      );
                    })
                  }
                />
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
